import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Config } from 'src/app/models/config.model';
import { Country } from 'src/app/models/country.model';
import { CaseService } from 'src/app/services/case.service';
import { ConfigService } from 'src/app/services/config.service';
import { ContentService } from 'src/app/services/content.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { TitleService } from 'src/app/services/title.service';
import { phoneValidator } from 'src/app/validators/phone.validator';
import { NewContactComponent } from '../../dialog/new-contact/new-contact.component';
import { RemoveContactComponent } from '../../dialog/remove-contact/remove-contact.component';
import { SnackbarMessageComponent } from '../../snackbar/snackbar-message/snackbar-message.component';
import { CaseModel } from 'src/app/models/case.model';
import { ComplainerAddressModel, ComplainerModel, ComplainerPhoneModel } from 'src/app/models/complainer.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IContentComponent } from 'src/app/interfaces/content-component.interface';
import { CanComponentDeactivate } from 'src/app/guards/case-changed.guard';
import { ConfirmBoxComponent, DialogData } from '../../dialog/confirm-box/confirm-box.component';
import { ContactInvitationService } from 'src/app/services/contact-invitation.service';
import { multipleEmailValidator } from 'src/app/validators/email.validator';
import { UploadService } from 'src/app/services/upload.service';

@Component({
  selector: 'app-case-detail',
  templateUrl: './case-detail.component.html',
  styleUrls: ['./case-detail.component.scss']
})
export class CaseDetailComponent implements OnInit, OnDestroy, CanComponentDeactivate, IContentComponent {

  private componentDestroyed = new Subject();

  pageComponentName: string = 'CaseDetails';
  data!: Map<string, string>;

  case!: CaseModel;
  errorObject = null;

  caseForm!: FormGroup;

  config!: Config;
  filteredCountries!: Observable<Country[]>;

  editModeComplainer: boolean = false;
  editModeCorrespondent: boolean = false;

  showAlertInfo: boolean = false;
  eventsVisible: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private contentService: ContentService,
    private contactInvitationService: ContactInvitationService,
    private caseService: CaseService,
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
    private matDialog: MatDialog,
    private spinnerService: SpinnerService,
    private uploadService: UploadService,
    private titleService: TitleService) {

    this.titleService.setTitle("Finansklagenemnda - Min sak");

    this.configService.config
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (c: Config | null) => {
          if (c !== null) {
            this.config = c;
            this.filteredCountries = this.getFilteredCountries();
          }
        }
      );

    this.contentService.content
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (data: Map<string, string>) => {
          if (data !== undefined) {
            this.data = data;
          }
        }
      );

    this.contentService.loadContent('pageText', this.pageComponentName, ['text', 'label']);

    this.caseService.currentCase
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: CaseModel | null) => {
          if (value !== null) {
            this.case = value;
            if (this.case.IsClientPaperless) {
              this.initForm();
            }
          }
        }
      );


    this.route.params
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (params) => {
          let caseUId = params['id'];
          this.caseService.loadCase(caseUId);
        }
      );
  }

  getLabelValue(label: string): string {
    let value: string = '';
    if (this.data) {
      value = this.data.get(label) ?? '';
    }
    return value;
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(null);
    this.componentDestroyed.unsubscribe();
  }

  getPhoneDisplayValueFromCountryCode(countryCode: string, withName: boolean): string {
    var displayValue: string = '';

    if (countryCode && countryCode !== '') {
      var country: Country | undefined = this.config?.Countries.find(c => c.cca2 === countryCode);
      if (country) {
        if (country.callingcodes && country.callingcodes.length > 0) {
          displayValue = country.callingcodes[0];
        }
        if (withName) {
          displayValue = country.name.common + ' (' + displayValue + ')';
        }
      }
    }

    return displayValue;
  }

  getFilteredCountries(): Observable<Country[]> {
    //var codes = this.config.Countries.filter(z => z.region.toLowerCase() == 'europe').filter(x => x.callingcodes && x.callingcodes.length > 0)
    var codes = this.config.Countries.filter(x => x.callingcodes && x.callingcodes.length > 0);
    return of(codes);
  }

  initForm() {

    this.caseForm = this.fb.group({

      firstName: [{ value: this.case.Complainer.FirstName, disabled: true }, [Validators.maxLength(100)]],
      lastName: [{ value: this.case.Complainer.LastName, disabled: true }, [Validators.maxLength(100)]],
      address: [{ value: this.case.Complainer.Address !== null ? this.case.Complainer.Address.Address : null, disabled: true }, [Validators.maxLength(200)]],
      postalCode: [{ value: this.case.Complainer.Address !== null ? this.case.Complainer.Address.PostalCode : null, disabled: true }],
      postalPlace: [{ value: this.case.Complainer.Address !== null ? this.case.Complainer.Address.PostalPlace : null, disabled: true }],
      countryCodeAlpha2: [{ value: this.case.Complainer.Address !== null && this.case.Complainer.Address.CountryCodeAlpha2 ? this.case.Complainer.Address.CountryCodeAlpha2 : 'NO', disabled: true }],
      phone: [{ value: this.case.Complainer.Phone !== null ? this.case.Complainer.Phone.Phone : '', disabled: true }],
      phoneCountryCodeAlpha2: [{ value: this.case.Complainer.Phone !== null && this.case.Complainer.Phone.CountryCodeAlpha2 ? this.case.Complainer.Phone.CountryCodeAlpha2 : 'NO', disabled: true }],
      email: [{ value: this.case.Complainer.Email, disabled: true }, [Validators.required, Validators.maxLength(100), multipleEmailValidator()]],
      reference: [{ value: this.case.Complainer.Reference, disabled: true }, [Validators.maxLength(100)]],
      orgnr: [{ value: this.case.Complainer != null ? this.case.Complainer.Orgnr : null, disabled: true }, [Validators.maxLength(100)]],

      correspondentFirstName: [{ value: this.case.Correspondent != null ? this.case.Correspondent.FirstName : null, disabled: true }, [Validators.maxLength(100)]],
      correspondentLastName: [{ value: this.case.Correspondent != null ? this.case.Correspondent.LastName : null, disabled: true }, [Validators.maxLength(100)]],
      correspondentAddress: [{ value: this.case.Correspondent != null && this.case.Correspondent.Address != null ? this.case.Correspondent.Address.Address : null, disabled: true }, [Validators.maxLength(200)]],
      correspondentPostalCode: [{ value: this.case.Correspondent != null && this.case.Correspondent.Address != null ? this.case.Correspondent.Address.PostalCode : null, disabled: true }],
      correspondentPostalPlace: [{ value: this.case.Correspondent != null && this.case.Correspondent.Address != null ? this.case.Correspondent.Address.PostalPlace : null, disabled: true }],
      correspondentCountryCodeAlpha2: [{ value: this.case.Correspondent != null && this.case.Correspondent.Address != null ? this.case.Correspondent.Address.CountryCodeAlpha2 : 'NO', disabled: true }],
      correspondentPhone: [{ value: this.case.Correspondent != null && this.case.Correspondent.Phone !== null ? this.case.Correspondent.Phone.Phone : null, disabled: true }],
      correspondentPhoneCountryCodeAlpha2: [{ value: this.case.Correspondent !== null && this.case.Correspondent.Phone !== null ? this.case.Correspondent.Phone.CountryCodeAlpha2 : 'NO', disabled: true }],
      correspondentEmail: [{ value: this.case.Correspondent != null ? this.case.Correspondent.Email : null, disabled: true }, [Validators.required, Validators.maxLength(100), multipleEmailValidator()]],
      correspondentReference: [{ value: this.case.Correspondent != null ? this.case.Correspondent.Reference : null, disabled: true }, [Validators.maxLength(100)]],
      correspondentOrgNr: [{ value: this.case.Correspondent != null ? this.case.Correspondent.Orgnr : null, disabled: true }, [Validators.maxLength(100)]]
    });

    this.caseForm.controls.phone.setValidators([Validators.required, phoneValidator(this.caseForm.controls.phoneCountryCodeAlpha2)]);
    this.caseForm.controls.correspondentPhone.setValidators([Validators.required, phoneValidator(this.caseForm.controls.correspondentPhoneCountryCodeAlpha2)]);

    if (this.case.Correspondent === null) {
      this.caseForm.controls.address.addValidators([Validators.required]);

      if (this.caseForm.controls.countryCodeAlpha2.value === 'NO') {
        this.caseForm.controls.postalCode.setValidators(Validators.required);
      }
    }
    else {

      this.caseForm.controls.correspondentAddress.addValidators([Validators.required]);

      if (this.caseForm.controls.correspondentCountryCodeAlpha2.value === 'NO') {
        this.caseForm.controls.correspondentPostalCode.setValidators(Validators.required);
      }

      this.caseForm.controls.correspondentEmail.addValidators([Validators.required]);
    }

    this.caseForm.controls.phoneCountryCodeAlpha2.valueChanges
      .subscribe(
        (value: string) => {
          this.caseForm.controls.phone.updateValueAndValidity(); //check if valid with new country code
        }
      );

    this.caseForm.controls.countryCodeAlpha2.valueChanges
      .subscribe(
        (value: string) => {

          if (value === 'NO') {
            this.caseForm.controls.postalCode.setValidators(Validators.required);
          } else {
            this.caseForm.controls.postalCode.clearValidators();
          }
          this.caseForm.controls.postalCode.updateValueAndValidity();

          this.caseForm.controls.phoneCountryCodeAlpha2.setValue(value);
          this.caseForm.controls.phone.updateValueAndValidity(); //check if valid with new country code
        }
      );

    this.caseForm.controls.postalCode.valueChanges
      .subscribe(
        (value: string) => {
          if (value && value.length === 4) {
            this.caseService.loadPostalPlace(value)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe(
                (value: string) => {
                  if (value === 'Ugyldig postnummer') {
                    this.caseForm.controls.postalPlace.setValue('');
                  } else {
                    this.caseForm.controls.postalPlace.setValue(value);
                  }
                }
              );
          } else {
            this.caseForm.controls.postalPlace.setValue('');
          }
        }
      );

    this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.valueChanges
      .subscribe(
        (value: string) => {
          this.caseForm.controls.correspondentPhone.updateValueAndValidity(); //check if valid with new country code
        }
      );

    this.caseForm.controls.correspondentCountryCodeAlpha2.valueChanges
      .subscribe(
        (value: string) => {
          if (value === 'NO') {
            this.caseForm.controls.correspondentPostalCode.setValidators(Validators.required);
          } else {
            this.caseForm.controls.correspondentPostalCode.clearValidators();
          }
          this.caseForm.controls.correspondentPostalCode.updateValueAndValidity();

          this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.setValue(value);
          this.caseForm.controls.correspondentPhone.updateValueAndValidity(); //check if valid with new country code
        }
      );

    this.caseForm.controls.correspondentPostalCode.valueChanges
      .subscribe(
        (value: string) => {
          if (value && value.length === 4) {
            this.caseService.loadPostalPlace(value)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe(
                (value: string) => {
                  if (value === 'Ugyldig postnummer') {
                    this.caseForm.controls.correspondentPostalPlace.setValue('');
                  } else {
                    this.caseForm.controls.correspondentPostalPlace.setValue(value);
                  }
                }
              );
          } else {
            this.caseForm.controls.correspondentPostalPlace.setValue('');
          }
        }
      );
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {

    if (this.uploadService.files.length > 0) {
      return this.openCaseChangedDialog('Advarsel: Du har lastet opp en fil for å sende inn et brev. Hvis du forlater siden, vil du miste endringene.')
      .pipe(
        map(
          (val: boolean) => {
            if (val) {
              console.log('clearing files')
              this.uploadService.clearFiles(true, false);
            }
            return val; 
          }
        )
      )
    }


    if (this.editModeCorrespondent) {
      if (this.caseForm.dirty && this.caseForm.touched) {
        return this.openCaseChangedDialog('Advarsel: Opplysninger om kontaktperson er endret. Hvis du forlater siden, vil du miste endringene.');
      }
    }

    if (this.editModeComplainer) {
      if (this.caseForm.dirty && this.caseForm.touched) {
        return this.openCaseChangedDialog('Advarsel: Opplysninger om klager er endret. Hvis du forlater siden, vil du miste endringene.');
      }
    }

    return true;
  }

  openCaseChangedDialog(message: string): Observable<boolean> {

    let initialState: DialogData = {
      body: message,
      title: 'Forlate siden?',
      okBtnName: 'Ok',
      closeBtnName: 'Avbryt'
    };

    const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { data: initialState })
    return dialogRef.afterClosed().pipe(map(result => result === undefined ? false : result));
  }

  toggleEventsView(value: boolean): void {
    this.eventsVisible = value;
  }

  toggleAlertInfo() {
    this.showAlertInfo = !this.showAlertInfo;
  }

  toggleEditModeComplainer(mode: boolean): void {
    this.editModeComplainer = mode;
    this.toggleEnabledComplainerValues();
  }

  cancelEditModeComplainer(): void {
    this.caseForm.controls.firstName.setValue(this.case.Complainer.FirstName);
    this.caseForm.controls.lastName.setValue(this.case.Complainer.LastName);
    this.caseForm.controls.address.setValue(this.case.Complainer.Address.Address);
    this.caseForm.controls.postalCode.setValue(this.case.Complainer.Address.PostalCode);
    this.caseForm.controls.postalPlace.setValue(this.case.Complainer.Address.PostalPlace);
    this.caseForm.controls.countryCodeAlpha2.setValue(this.case.Complainer.Address !== null && this.case.Complainer.Address.CountryCodeAlpha2 !== null ? this.case.Complainer.Address.CountryCodeAlpha2 : 'NO');
    this.caseForm.controls.phone.setValue(this.case.Complainer.Phone?.Phone);
    this.caseForm.controls.phoneCountryCodeAlpha2.setValue(this.case.Complainer.Phone !== null && this.case.Complainer.Phone.CountryCodeAlpha2 !== null ? this.case.Complainer.Phone.CountryCodeAlpha2 : 'NO');
    this.caseForm.controls.email.setValue(this.case.Complainer.Email);

    this.toggleEditModeComplainer(false);
  }

  toggleEnabledComplainerValues() {
    if (this.editModeComplainer) {
      this.caseForm.controls.address.enable({ emitEvent: false });
      this.caseForm.controls.postalCode.enable({ emitEvent: false });
      this.caseForm.controls.countryCodeAlpha2.enable({ emitEvent: false });
      this.caseForm.controls.phone.enable({ emitEvent: false });
      this.caseForm.controls.phoneCountryCodeAlpha2.enable({ emitEvent: false });
      this.caseForm.controls.email.enable({ emitEvent: false });
      this.caseForm.controls.reference.enable({ emitEvent: false });      
    } else {
      this.caseForm.controls.firstName.disable({ emitEvent: false });
      this.caseForm.controls.lastName.disable({ emitEvent: false });
      this.caseForm.controls.address.disable({ emitEvent: false });
      this.caseForm.controls.postalCode.disable({ emitEvent: false });
      this.caseForm.controls.countryCodeAlpha2.disable({ emitEvent: false });
      this.caseForm.controls.phone.disable({ emitEvent: false });
      this.caseForm.controls.phoneCountryCodeAlpha2.disable({ emitEvent: false });
      this.caseForm.controls.email.disable({ emitEvent: false });
      this.caseForm.controls.reference.disable({ emitEvent: false });
    }
  }

  saveComplainer(): void {

    if (!this.caseForm.valid) {
      this.caseForm.markAllAsTouched();
      return;
    }

    if (this.case != null) {

      this.case.Complainer.FirstName = this.case.Complainer.LastName; // Read-only value //this.caseForm.controls.firstName.value;
      this.case.Complainer.LastName = this.case.Complainer.FirstName; // Read-only value //this.caseForm.controls.lastName.value;
      this.case.Complainer.Email = this.caseForm.controls.email.value;
      this.case.Complainer.Reference = this.caseForm.controls.reference.value;
      this.case.Complainer.Orgnr = this.caseForm.controls.orgnr.value;

      let address: ComplainerAddressModel = {
        Address: this.caseForm.controls.address.value,
        PostalCode: this.caseForm.controls.postalCode.value,
        PostalPlace: this.caseForm.controls.postalPlace.value,
        CountryCodeAlpha2: this.caseForm.controls.countryCodeAlpha2.value
      }

      this.case.Complainer.Address = address;

      let phone: ComplainerPhoneModel = {
        Phone: this.caseForm.controls.phone.value,
        CountryCodeAlpha2: this.caseForm.controls.phoneCountryCodeAlpha2.value
      }

      this.case.Complainer.Phone = phone;

      this.save();
      this.toggleEditModeComplainer(false);

    }

  }

  toggleEditModeCorrespondent(mode: boolean): void {
    this.editModeCorrespondent = mode;
    this.toggleEnabledCorrespondentValues();
  }

  cancelEditModeCorrespondent(): void {
    this.caseForm.controls.correspondentFirstName.setValue(this.case.Correspondent.FirstName);
    this.caseForm.controls.correspondentLastName.setValue(this.case.Correspondent.LastName);
    this.caseForm.controls.correspondentAddress.setValue(this.case.Correspondent.Address.Address);
    this.caseForm.controls.correspondentPostalCode.setValue(this.case.Correspondent.Address.PostalCode);
    this.caseForm.controls.correspondentPostalPlace.setValue(this.case.Correspondent.Address.PostalPlace);
    this.caseForm.controls.correspondentCountryCodeAlpha2.setValue(this.case.Correspondent.Address.CountryCodeAlpha2);
    this.caseForm.controls.correspondentPhone.setValue(this.case.Correspondent.Phone?.Phone);
    this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.setValue(this.case.Correspondent.Phone?.CountryCodeAlpha2);
    this.caseForm.controls.correspondentEmail.setValue(this.case.Correspondent.Email);
    this.caseForm.controls.correspondentReference.setValue(this.case.Correspondent.Reference);


    this.toggleEditModeCorrespondent(false);
  }

  toggleEnabledCorrespondentValues(): void {

    if (this.editModeCorrespondent) {
      this.caseForm.controls.correspondentAddress.enable({ emitEvent: false });
      this.caseForm.controls.correspondentPostalCode.enable({ emitEvent: false });
      this.caseForm.controls.correspondentCountryCodeAlpha2.enable({ emitEvent: false });
      this.caseForm.controls.correspondentPhone.enable({ emitEvent: false });
      this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.enable({ emitEvent: false });
      this.caseForm.controls.correspondentEmail.enable({ emitEvent: false });
      this.caseForm.controls.correspondentReference.enable({ emitEvent: false });
    }
    else {
      this.caseForm.controls.correspondentFirstName.disable({ emitEvent: false });
      this.caseForm.controls.correspondentLastName.disable({ emitEvent: false });
      this.caseForm.controls.correspondentAddress.disable({ emitEvent: false });
      this.caseForm.controls.correspondentPostalCode.disable({ emitEvent: false });
      this.caseForm.controls.correspondentCountryCodeAlpha2.disable({ emitEvent: false });
      this.caseForm.controls.correspondentPhone.disable({ emitEvent: false });
      this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.disable({ emitEvent: false });
      this.caseForm.controls.correspondentEmail.disable({ emitEvent: false });
      this.caseForm.controls.correspondentReference.disable({ emitEvent: false });
    }

  }

  getAlternativePlaceholder(defaultLabel: string, isComplainer: boolean): string {

    if (isComplainer) {
      if (this.case.Complainer && this.case.Complainer.IsFirm) {
        if (defaultLabel === 'Fornavn') {
          return 'Firma';
        } else {
          return 'Representant';
        }
      }
      else {
        return defaultLabel;
      }
    }
    else {
      if (this.case.Correspondent && this.case.Correspondent.IsFirm) {
        if (defaultLabel === 'Fornavn') {
          return 'Firma';
        } else {
          return 'Att';
        }
      }
      else {
        return defaultLabel;
      }
    }

  }

  saveCorrespondent(): void {

    if (!this.caseForm.valid) {
      this.caseForm.markAllAsTouched();
      return;
    }

    if (this.case != null) {

      let address: ComplainerAddressModel = {
        Address: this.caseForm.controls.correspondentAddress.value,
        PostalCode: this.caseForm.controls.correspondentPostalCode.value,
        PostalPlace: this.caseForm.controls.correspondentPostalPlace.value,
        CountryCodeAlpha2: this.caseForm.controls.correspondentCountryCodeAlpha2.value
      }

      let phone: ComplainerPhoneModel = {
        Phone: this.caseForm.controls.correspondentPhone.value,
        CountryCodeAlpha2: this.caseForm.controls.correspondentPhoneCountryCodeAlpha2.value
      }

      let correspondent: ComplainerModel = {
        FirstName: this.case.Correspondent.FirstName, //Read-only //this.caseForm.controls.correspondentFirstName.value,
        LastName: this.case.Correspondent.LastName, //Read-only //this.caseForm.controls.correspondentLastName.value,
        Email: this.caseForm.controls.correspondentEmail.value,
        Reference: this.caseForm.controls.correspondentReference.value,
        Orgnr: this.caseForm.controls.correspondentOrgNr.value,
        Phone: phone,
        Address: address,
        IsFirm: this.case.Complainer.IsFirm
      }

      this.case.Correspondent = correspondent;

      this.save();
      this.toggleEditModeCorrespondent(false);

    }

  }

  save(): void {

    this.spinnerService.show("Lagrer opplysninger");
    this.caseService.save(this.case)
      .subscribe(
        {
          next: () => {
            this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: 'Endringene ble lagret', type: 'info' } });
            this.caseService.loadCase(this.case.CaseUid);
          },
          error: (error: HttpErrorResponse) => {
            console.log(error.error.Message);
            //this.airbrakeService.error(error);
            //this.airbrake.handleError(error);
            this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: error.error.Message, type: 'error' } });
            this.spinnerService.hide();
          },
          complete: () => {
            console.log('Finished save case with id ' + this.case.CaseUid);
            this.spinnerService.hide();
          }
  
        }
      );
  }

  addContact(): void {

    var callback = (result: boolean | undefined) => {
      console.log(`Dialog result: ${result}`);
      if (result) {
        this.caseService.loadCase(this.case.CaseUid);
      }
    }
    this.openContactDialog(true, callback);
  }

  changeContact(): void {

    if (!this.case.IsComplainer) {
      let initialState: DialogData = {
        body: this.case?.HasContactInvitation ? this.getLabelValue('NewContactWaiting') : this.getLabelValue('PopUpChangeContact'),
        title: 'Bytte kontaktperson?',
        okBtnName: 'Ok',
        closeBtnName: 'Avbryt'
      };

      const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState })
      dialogRef
        .afterClosed()
        .subscribe(
          (value: boolean | undefined) => {
            console.log(value);
            if (value === true) {
              this.changeContactConfirmed()
            }
          }
        );
    }
    else {
      this.changeContactConfirmed();
    }
  }
  changeContactConfirmed(): void {
    var callback = (result: boolean | undefined) => {
      console.log(`Dialog result: ${result}`);
      if (result) {
        this.caseService.loadCase(this.case.CaseUid);
      }
    }
    this.openContactDialog(false, callback);
  }

  removeContact(): void {

    if (this.case.HasContactInvitation || !this.case.IsComplainer) {
      let body: string =
        this.case.HasComplainerKey ? this.case.HasContactInvitation ? this.getLabelValue('PopUpHasContactInvitation') : this.getLabelValue('PopUpRemoveContact') : 
          this.getLabelValue('RemoveElCom');
      
      let initialState: DialogData = {
        body: body,
        title: 'Fjerne kontaktperson?',
        okBtnName: 'Ok',
        closeBtnName: 'Avbryt'
      };

      const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState })
      dialogRef
        .afterClosed()
        .subscribe(
          (value: boolean | undefined) => {
            console.log(value);
            if (value === true) {
              this.removeContactConfirmed()
            }
          }
        );
    }
    else {
      this.removeContactConfirmed();
    }
  }
  removeContactConfirmed(): void {

    const dialogRef: MatDialogRef<RemoveContactComponent, boolean> = this.matDialog.open(RemoveContactComponent, {
      width: '600px',
      data: {
        Case: this.case,
        HasComplainerKey: this.case.HasComplainerKey,
        RemoveElComMessage: this.getLabelValue('RemoveElCom')
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (result: boolean | undefined) => {
          if (result === true) {
            this.spinnerService.show("Fjerner kontaktperson på saken");
            this.contactInvitationService.removeContact(this.case.CaseUid)
              .subscribe(
                () => {
                  this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: 'Kontaktpersonen ble fjernet', type: 'info' } });
                  if (!this.case.IsComplainer) {
                    this.router.navigate(['cases']);
                  }
                  else {
                    this.caseService.loadCase(this.case.CaseUid);
                  }
                },
                (error: HttpErrorResponse) => {
                  console.log(error.error.Message);
                  this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: error.error.Message, type: 'error' } });
                  this.spinnerService.hide();
                },
                () => {
                  console.log('Finished remove contact for case ' + this.case.CaseUid);
                  this.spinnerService.hide();
                }
              );
          }
        });
  }

  openContactDialog(isAdding: boolean, callback: (value: boolean | undefined) => void): void {
    const dialogRef: MatDialogRef<NewContactComponent, boolean> = this.matDialog.open(NewContactComponent, {
      data: {
        Case: this.case,
        IsAdding: isAdding,
        WaitingMessage: this.getLabelValue('NewContactWaiting'),
        ConfirmMessage: this.getLabelValue('ConfirmInformation'),
        PopUpConfirmMesage: this.getLabelValue('PopUpConfirm')
      },
      width: '800px'
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (result: boolean | undefined) => {
          callback(result);
        });
  }

  inviteComplainer(): void {
    
    if(this.case.Complainer?.Phone === null || this.case.Complainer?.Phone.Phone === null || this.case.Complainer?.Phone.Phone === '') {
      let initialState: DialogData = {
        body: 'Telefonnummer for klager må fylles ut før klager kan inviteres til Min sak',
        title: 'Manglende opplysninger',
        okBtnName: 'Ok',
        closeBtnName: ''
      };

      this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState });
    }
    else if (this.case.Complainer?.Email === null || this.case.Complainer?.Email === '') {
      let initialState: DialogData = {
        body: 'E-postadresse for klager må fylles ut før klager kan inviteres til Min sak',
        title: 'Manglende opplysninger',
        okBtnName: 'Ok',
        closeBtnName: ''
      };

      this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState });

    }
    else {
      this.inviteComplainerConfirmed();
    }
  }

  inviteComplainerConfirmed(): void {

    let initialState: DialogData = {
      body: this.getLabelValue('PopUpNeedContactDetails'),
      title: 'Invitere klager',
      okBtnName: 'Ok',
      closeBtnName: 'Avbryt'
    };

    const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState })
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (result: boolean | undefined) => {
          if (result === true) {
            if (this.allContanctInformationIsValid()) {
              this.spinnerService.show('Inviterer klager til Min sak...');

              this.contactInvitationService.addComplainerInvitation(this.case.CaseUid)
              .subscribe(
                () => {
                  let message = `Invitasjon om tilgang til Min sak sendt til ${this.case.Complainer?.FirstName} ${this.case.Complainer?.LastName}`;
        
                  this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: message, type: 'info' } });
                  this.caseService.loadCase(this.case.CaseUid);
        
                },
                (error: HttpErrorResponse) => {
                  console.log(error.error.Message);
        
                  this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: error.error.Message, type: 'error' } });
                  this.spinnerService.hide();
                },
                () => {
                  console.log('Finished added complainer invitation for case id ' + this.case.CaseUid);
                  this.spinnerService.hide();
                }
              );
            }  
          }
        });
  }

  private allContanctInformationIsValid(): boolean {
    return !this.isNullOrEmpty(this.case.Complainer?.FirstName) &&
      !this.isNullOrEmpty(this.case.Complainer?.LastName) &&
      !this.isNullOrEmpty(this.case.Complainer?.Email) &&
      !this.isNullOrEmpty(this.case.Complainer?.Phone?.Phone) &&
      !this.isNullOrEmpty(this.case.Complainer?.Phone?.CountryCodeAlpha2) &&
      !this.isNullOrEmpty(this.case.Correspondent?.FirstName) &&
      !this.isNullOrEmpty(this.case.Correspondent?.LastName) &&
      !this.isNullOrEmpty(this.case.Correspondent?.Email) &&
      !this.isNullOrEmpty(this.case.Correspondent?.Phone?.Phone) &&
      !this.isNullOrEmpty(this.case.Correspondent?.Phone?.CountryCodeAlpha2)
  }

  private isNullOrEmpty(value: string | null | undefined): boolean {
    return value === undefined || value === null || value === '';
  }

}
